import { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, Sparkles, ShoppingBag, Plus, Minus, X, Trash2, Truck, Smartphone } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CART_KEY = "jjax_cart";
const FREE_SHIP_THRESHOLD = 60;

const RANGE_ORDER = [
  { brand: "Davines", range: "LOVE Curl", label: "Davines LOVE Curl", blurb: "My go-to curl range — moisture, definition and up to 72 hours of frizz control." },
  { brand: "Davines", range: "OI", label: "Davines OI", blurb: "Absolute beautifying — softness, shine and body for every hair type." },
  { brand: "Davines", range: "MORE INSIDE", label: "Davines MORE INSIDE", blurb: "Curl styling essentials to build, define and relax your natural texture." },
  { brand: "Davroe", range: "CURLiCUE", label: "Davroe CURLiCUE", blurb: "Vegan, cruelty-free curl care from Australia." },
];

function ProductImage({ product }) {
  const [errored, setErrored] = useState(false);
  if (!product.image || errored) {
    return (
      <div className="aspect-square w-full bg-gradient-to-br from-sage/25 to-peachlight flex flex-col items-center justify-center p-6 text-center">
        <span className="font-accent text-lg text-olive/70 mb-1">{product.brand}</span>
        <span className="font-display text-sm text-charcoal/70 leading-snug">{product.name}</span>
        <span className="text-[10px] uppercase tracking-widest text-charcoal/40 mt-2">Photo coming soon</span>
      </div>
    );
  }
  return (
    <div className="aspect-square bg-white flex items-center justify-center p-4">
      <img
        src={product.image}
        alt={product.name}
        className="max-h-full max-w-full object-contain"
        loading="lazy"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(() => {
    try { return JSON.parse(localStorage.getItem(CART_KEY)) || {}; } catch { return {}; }
  });
  const [cartOpen, setCartOpen] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [donateSas, setDonateSas] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/shop/products`)
      .then((r) => setProducts(r.data))
      .catch(() => toast.error("Failed to load products"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  const byLookup = useMemo(() => {
    const m = {};
    products.forEach((p) => { m[p.lookup_key] = p; });
    return m;
  }, [products]);

  const addToCart = (product) => {
    const inStock = product.stock ?? 0;
    setCart((c) => {
      const current = c[product.lookup_key] || 0;
      if (current >= inStock) {
        toast.error(`Only ${inStock} in stock`);
        return c;
      }
      toast.success(`${product.name} added to bag`);
      return { ...c, [product.lookup_key]: current + 1 };
    });
  };

  const setQty = (lookupKey, qty) => {
    const max = byLookup[lookupKey]?.stock ?? 0;
    setCart((c) => {
      const next = { ...c };
      if (qty <= 0) delete next[lookupKey];
      else next[lookupKey] = Math.min(qty, max);
      return next;
    });
  };

  const cartEntries = Object.entries(cart)
    .map(([lookup_key, quantity]) => ({ product: byLookup[lookup_key], quantity }))
    .filter((e) => e.product);
  const cartCount = cartEntries.reduce((n, e) => n + e.quantity, 0);
  const subtotal = cartEntries.reduce((s, e) => s + e.product.price * e.quantity, 0);
  const freeShipping = subtotal >= FREE_SHIP_THRESHOLD;

  const handleCheckout = async () => {
    if (cartCount === 0) return;
    setCheckingOut(true);
    try {
      const items = cartEntries.map((e) => ({ lookup_key: e.product.lookup_key, quantity: e.quantity }));
      const res = await axios.post(`${API}/shop/checkout`, {
        items,
        donate_sas: donateSas,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.checkout_url;
    } catch {
      toast.error("Could not start checkout — please try again.");
      setCheckingOut(false);
    }
  };

  return (
    <div className="pt-28 lg:pt-44 pb-14" data-testid="products-page">
      {/* Hero */}
      <section className="py-10 text-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-3xl mx-auto">
          <h1 className="font-display text-4xl sm:text-5xl text-black mb-4" data-testid="products-heading">
            Shop Curl Care
          </h1>
          <p className="text-charcoal/70 text-sm md:text-base leading-relaxed max-w-xl mx-auto">
            Salon-grade curl and hair care, hand-picked for every texture. Order online for UK Tracked
            delivery or choose free Click & Collect from the Bude salon.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-xs font-medium text-olive bg-sage/15 rounded-full px-4 py-1.5" data-testid="postage-banner">
            <Truck size={14} /> Complimentary UK delivery over £{FREE_SHIP_THRESHOLD} · Free Click & Collect
          </div>
          <div className="mt-5">
            <Link
              to="/curlscan"
              className="bg-olive text-white hover:bg-olive/80 rounded-full px-6 py-2.5 text-sm font-medium transition-all inline-flex items-center gap-2"
              data-testid="curl-edit-hero-btn"
            >
              <Smartphone size={16} /> Not sure what your curls need? Try The Curl Edit
            </Link>
          </div>
        </motion.div>
      </section>

      {/* About the brands / why I chose them */}
      <section className="max-w-6xl mx-auto px-6 my-10" data-testid="about-brands">
        <div className="text-center max-w-2xl mx-auto mb-8">
          <p className="font-accent text-xl text-sunset mb-1">chosen with care</p>
          <h2 className="font-display text-3xl sm:text-4xl text-black mb-3">Why These Brands</h2>
          <p className="text-charcoal/70 text-sm md:text-base leading-relaxed">
            I only stock what I trust on my own clients and my own curls. Here's a little about the
            two brands I've chosen and why.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-white/70 rounded-2xl p-7 border border-black/5" data-testid="about-davines">
            <img
              src="/brands/davines.svg"
              alt="Davines logo"
              className="h-8 w-auto object-contain mb-3"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="logo-davines"
            />
            <h3 className="font-display text-2xl text-black mb-2">Davines</h3>
            <p className="text-xs uppercase tracking-widest text-charcoal/40 mb-4">LOVE Curl · OI · MORE INSIDE</p>
            <p className="text-sm text-charcoal/70 leading-relaxed">
              Davines is more than just a haircare brand. Alongside producing high-quality products,
              they're committed to sustainability, innovation and education. They offer a huge amount
              of free training to support salons and self-employed stylists, helping us continue
              learning throughout our careers. They also work towards reducing waste through more
              sustainable packaging and recycling initiatives with salon partners. They're a company
              that cares about the bigger picture as well as creating products that genuinely perform.
            </p>
          </div>

          <div className="bg-white/70 rounded-2xl p-7 border border-black/5" data-testid="about-davroe">
            <img
              src="/brands/davroe.png"
              alt="Davroe logo"
              className="h-9 w-auto object-contain mb-3"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              data-testid="logo-davroe"
            />
            <h3 className="font-display text-2xl text-black mb-2">Davroe</h3>
            <p className="text-xs uppercase tracking-widest text-charcoal/40 mb-4">CURLiCUE</p>
            <p className="text-sm text-charcoal/70 leading-relaxed">
              Davroe creates high-quality, Australian-made haircare that's vegan, cruelty-free and
              made with carefully selected ingredients. The focus is on products that are gentle,
              effective and do exactly what they promise. It's a brand that's built a strong
              reputation for quality and consistency, making it an easy choice for anyone looking
              after the long-term health of their hair.
            </p>
          </div>
        </div>
      </section>

      {/* Product sections by range */}
      <section className="max-w-6xl mx-auto px-6">
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-charcoal/30" size={32} />
          </div>
        ) : (
          RANGE_ORDER.map((group) => {
            const groupProducts = products.filter((p) => p.brand === group.brand && p.range === group.range);
            if (groupProducts.length === 0) return null;
            return (
              <div key={group.label} className="mb-14" data-testid={`range-${group.range.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="mb-6">
                  <h2 className="font-display text-2xl sm:text-3xl text-black">{group.label}</h2>
                  <p className="text-charcoal/60 text-sm mt-1 max-w-2xl">{group.blurb}</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {groupProducts.map((product, i) => (
                    <motion.div
                      key={product.id}
                      initial={{ opacity: 0, y: 12 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: i * 0.03 }}
                      className="bg-white/70 rounded-2xl overflow-hidden border border-black/5 hover:shadow-md transition-shadow flex flex-col"
                      data-testid={`product-card-${product.id}`}
                    >
                      <ProductImage product={product} />
                      <div className="p-5 flex flex-col flex-1">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <div>
                            <p className="text-[10px] uppercase tracking-widest text-charcoal/40 font-medium">
                              {product.brand} · {product.range}
                            </p>
                            <h3 className="font-display text-base text-black mt-0.5">{product.name}</h3>
                          </div>
                          {product.size && (
                            <span className="text-[10px] text-charcoal/50 bg-sage/20 rounded-full px-2 py-0.5 whitespace-nowrap mt-1">
                              {product.size}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-charcoal/60 leading-relaxed mb-4 flex-1">{product.desc}</p>
                        <div className="flex items-center justify-between mt-auto gap-3">
                          <div>
                            <span className="font-display text-lg text-black block" data-testid={`price-${product.id}`}>
                              £{product.price.toFixed(2)}
                            </span>
                            {product.stock > 0 ? (
                              <span className="text-[10px] text-olive font-medium" data-testid={`stock-${product.id}`}>
                                {product.stock} in stock
                              </span>
                            ) : (
                              <span className="text-[10px] text-red-500 font-medium" data-testid={`stock-${product.id}`}>
                                Out of stock
                              </span>
                            )}
                          </div>
                          {product.stock <= 0 ? (
                            <span className="bg-charcoal/10 text-charcoal/50 rounded-full px-4 py-2 text-xs font-medium" data-testid={`out-of-stock-${product.id}`}>
                              Sold out
                            </span>
                          ) : cart[product.lookup_key] ? (
                            <div className="flex items-center gap-2 bg-olive/10 rounded-full px-2 py-1" data-testid={`qty-control-${product.id}`}>
                              <button
                                onClick={() => setQty(product.lookup_key, cart[product.lookup_key] - 1)}
                                className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-olive hover:bg-olive hover:text-white transition-all"
                                data-testid={`qty-dec-${product.id}`}
                              >
                                <Minus size={13} />
                              </button>
                              <span className="text-sm font-medium w-5 text-center" data-testid={`qty-val-${product.id}`}>
                                {cart[product.lookup_key]}
                              </span>
                              <button
                                onClick={() => setQty(product.lookup_key, cart[product.lookup_key] + 1)}
                                disabled={cart[product.lookup_key] >= product.stock}
                                className="w-7 h-7 rounded-full bg-white flex items-center justify-center text-olive hover:bg-olive hover:text-white transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:text-olive"
                                data-testid={`qty-inc-${product.id}`}
                              >
                                <Plus size={13} />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => addToCart(product)}
                              className="bg-olive text-white hover:bg-olive/80 rounded-full px-4 py-2 text-xs font-medium transition-all flex items-center gap-1.5"
                              data-testid={`add-to-cart-${product.id}`}
                            >
                              <ShoppingBag size={13} /> Add to bag
                            </button>
                          )}
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </section>

      {/* The Curl Edit app */}
      <section className="max-w-6xl mx-auto px-6 my-8" data-testid="curl-edit-promo">
        <div className="bg-gradient-to-br from-sage/15 to-peachlight rounded-3xl border border-olive/10 grid md:grid-cols-2 items-center gap-6 p-8 md:p-12">
          <div>
            <span className="inline-flex items-center gap-2 bg-amber/10 text-amber text-xs font-medium tracking-wider uppercase px-4 py-1.5 rounded-full mb-4">
              <Sparkles size={13} /> Coming Soon
            </span>
            <h2 className="font-display text-3xl sm:text-4xl text-black mb-3">The Curl Edit App</h2>
            <p className="text-charcoal/70 text-sm md:text-base leading-relaxed mb-6 max-w-md">
              Your curls, decoded. Snap a photo, discover your curl type and get a personalised
              routine with the exact products to use — all from your pocket. Launching soon.
            </p>
            <Link
              to="/curlscan"
              className="bg-olive text-white hover:bg-eucalyptus rounded-full px-7 py-3 text-sm font-medium transition-all inline-flex items-center gap-2"
              data-testid="curl-edit-cta"
            >
              <Smartphone size={16} /> Join the waitlist
            </Link>
          </div>
          <div className="flex justify-center">
            <img
              src="/curl-edit-preview.jpg"
              alt="The Curl Edit app preview"
              className="max-h-[420px] w-auto object-contain drop-shadow-xl rounded-2xl"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* Consultation CTA */}
      <section className="py-10 text-center px-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="max-w-xl mx-auto">
          <h3 className="font-display text-2xl text-black mb-3">Not sure what your curls need?</h3>
          <p className="text-charcoal/70 text-sm mb-6">Book a consultation and I'll help you find the perfect routine.</p>
          <a
            href="https://tinyurl.com/2nxodcgu"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-olive text-white hover:bg-eucalyptus rounded-full px-8 py-3 text-sm font-medium transition-all inline-block"
            data-testid="products-book-btn"
          >
            Book a Consultation
          </a>
        </motion.div>
      </section>

      {/* Floating cart button */}
      <AnimatePresence>
        {cartCount > 0 && !cartOpen && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            onClick={() => setCartOpen(true)}
            className="fixed bottom-6 right-6 z-40 bg-charcoal text-white rounded-full pl-5 pr-6 py-3.5 shadow-xl flex items-center gap-3 hover:bg-black transition-all"
            data-testid="cart-button"
          >
            <span className="relative">
              <ShoppingBag size={20} />
              <span className="absolute -top-2 -right-2 bg-amber text-white text-[10px] font-bold w-4 h-4 rounded-full flex items-center justify-center" data-testid="cart-count">
                {cartCount}
              </span>
            </span>
            <span className="text-sm font-medium">£{subtotal.toFixed(2)}</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Cart drawer */}
      <AnimatePresence>
        {cartOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setCartOpen(false)}
              className="fixed inset-0 bg-black/40 z-50"
            />
            <motion.div
              initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
              transition={{ type: "tween", duration: 0.3 }}
              className="fixed top-0 right-0 h-full w-full max-w-md bg-warmcream z-50 flex flex-col shadow-2xl"
              data-testid="cart-drawer"
            >
              <div className="flex items-center justify-between p-5 border-b border-black/10">
                <h3 className="font-display text-xl text-black flex items-center gap-2">
                  <ShoppingBag size={18} /> Your Bag
                </h3>
                <button onClick={() => setCartOpen(false)} className="text-charcoal/50 hover:text-charcoal" data-testid="cart-close-btn">
                  <X size={22} />
                </button>
              </div>

              {cartEntries.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
                  <ShoppingBag size={40} className="text-charcoal/20 mb-3" />
                  <p className="text-charcoal/50 text-sm">Your bag is empty.</p>
                </div>
              ) : (
                <>
                  <div className="flex-1 overflow-y-auto p-5 space-y-4">
                    {cartEntries.map((e) => (
                      <div key={e.product.id} className="flex gap-3 items-center" data-testid={`cart-item-${e.product.id}`}>
                        <div className="w-16 h-16 rounded-xl overflow-hidden bg-white flex-shrink-0 border border-black/5">
                          <ProductImage product={e.product} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-black truncate">{e.product.name}</p>
                          <p className="text-xs text-charcoal/50">£{e.product.price.toFixed(2)}{e.product.size ? ` · ${e.product.size}` : ""}</p>
                          <div className="flex items-center gap-2 mt-1.5">
                            <button onClick={() => setQty(e.product.lookup_key, e.quantity - 1)} className="w-6 h-6 rounded-full bg-white border border-black/10 flex items-center justify-center text-charcoal hover:bg-olive hover:text-white transition-all" data-testid={`cart-dec-${e.product.id}`}>
                              <Minus size={12} />
                            </button>
                            <span className="text-sm w-5 text-center">{e.quantity}</span>
                            <button onClick={() => setQty(e.product.lookup_key, e.quantity + 1)} className="w-6 h-6 rounded-full bg-white border border-black/10 flex items-center justify-center text-charcoal hover:bg-olive hover:text-white transition-all" data-testid={`cart-inc-${e.product.id}`}>
                              <Plus size={12} />
                            </button>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-medium text-black">£{(e.product.price * e.quantity).toFixed(2)}</p>
                          <button onClick={() => setQty(e.product.lookup_key, 0)} className="text-charcoal/30 hover:text-red-500 mt-1" data-testid={`cart-remove-${e.product.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-black/10 p-5 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-charcoal/60">Subtotal</span>
                      <span className="font-medium text-black" data-testid="cart-subtotal">£{subtotal.toFixed(2)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-charcoal/60">
                      <Truck size={14} className="text-olive" />
                      {freeShipping ? (
                        <span className="text-olive font-medium">Your order qualifies for complimentary UK delivery!</span>
                      ) : (
                        <span>Add £{(FREE_SHIP_THRESHOLD - subtotal).toFixed(2)} more for complimentary UK delivery · UK Tracked from £3.95 · free Click & Collect</span>
                      )}
                    </div>
                    <label className="flex items-start gap-3 bg-sage/10 rounded-xl p-3 cursor-pointer" data-testid="donate-sas-toggle">
                      <input
                        type="checkbox"
                        checked={donateSas}
                        onChange={(e) => setDonateSas(e.target.checked)}
                        className="mt-0.5 w-4 h-4 accent-olive"
                        data-testid="donate-sas-checkbox"
                      />
                      <span className="text-xs text-charcoal/70 leading-relaxed">
                        <span className="font-medium text-charcoal">Add £1 to support Surfers Against Sewage</span>
                        <br />Collected by Justjjax and passed on in full to Surfers Against Sewage.
                      </span>
                    </label>
                    <button
                      onClick={handleCheckout}
                      disabled={checkingOut}
                      className="w-full bg-charcoal text-white hover:bg-black rounded-full py-3.5 text-sm font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="cart-checkout-btn"
                    >
                      {checkingOut ? <><Loader2 size={16} className="animate-spin" /> Redirecting to secure checkout…</> : `Checkout securely${donateSas ? " · +£1 donation" : ""}`}
                    </button>
                    <p className="text-[11px] text-center text-charcoal/40">Postage chosen at checkout · Secure payment by Stripe</p>
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
